/**
 * Public JD Seed Script
 * 
 * This script creates public job descriptions that candidates can select from
 * when taking self-service interviews.
 * 
 * Company ID: PUBLIC_JDS (special system company for public JDs)
 * 
 * Run this once to populate the database with public JDs.
 * Can be executed from browser console or as a standalone script.
 */

import { API, graphqlOperation } from 'aws-amplify';
import { createCompanyJobDescription, createCompanyProfile } from '../graphql/mutations';
import { listCompanyProfiles, listCompanyJobDescriptions } from '../graphql/queries';

// Public company configuration
const PUBLIC_COMPANY_CONFIG = {
  email: 'public-jds@getscreened.ai',
  companyName: 'GetScreened Public Jobs',
  companyUrl: 'getscreened.ai',
  userID: 'PUBLIC_JDS',
  t_c: true,
  location: 'Global',
  websiteUrl: 'https://getscreened.ai'
};

// Public JD templates for various roles
export const PUBLIC_JD_TEMPLATES = [
  {
    jobTitle: 'Software Engineer',
    selectedCategory: 'Engineering',
    selectedExperience: '0-3 years',
    skill: 'JavaScript, Python, Data Structures, Algorithms, Problem Solving, Git, REST APIs',
    jobDescription: `We are looking for a talented Software Engineer to join our team. 

Key Responsibilities:
- Design, develop, and maintain software applications
- Write clean, efficient, and well-documented code
- Collaborate with cross-functional teams
- Participate in code reviews and technical discussions
- Debug and resolve software issues

Requirements:
- Strong foundation in programming fundamentals
- Experience with at least one programming language (JavaScript, Python, Java, etc.)
- Understanding of data structures and algorithms
- Familiarity with version control systems (Git)
- Good problem-solving and analytical skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Learning Budget, Flexible Hours',
    workMode: 'Remote/Hybrid'
  },
  {
    jobTitle: 'Frontend Developer',
    selectedCategory: 'Engineering',
    selectedExperience: '1-3 years',
    skill: 'React, JavaScript, TypeScript, HTML, CSS, Tailwind CSS, Redux, REST APIs',
    jobDescription: `We are seeking a skilled Frontend Developer to create exceptional user experiences.

Key Responsibilities:
- Build responsive and interactive web applications using React
- Implement pixel-perfect UI designs
- Optimize applications for performance and accessibility
- Write reusable components and maintain code quality
- Collaborate with designers and backend developers

Requirements:
- Proficiency in React.js and modern JavaScript (ES6+)
- Strong understanding of HTML5, CSS3, and responsive design
- Experience with state management (Redux, Context API)
- Familiarity with TypeScript is a plus
- Knowledge of frontend build tools and bundlers`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Remote Work, Learning Budget',
    workMode: 'Remote'
  },
  {
    jobTitle: 'Backend Developer',
    selectedCategory: 'Engineering',
    selectedExperience: '1-4 years',
    skill: 'Node.js, Python, Java, SQL, MongoDB, REST APIs, Microservices, Docker',
    jobDescription: `We are looking for a Backend Developer to build robust and scalable server-side applications.

Key Responsibilities:
- Design and implement RESTful APIs and microservices
- Build and maintain database schemas and queries
- Ensure security, scalability, and performance of applications
- Write unit tests and integration tests
- Document APIs and technical specifications

Requirements:
- Strong experience with backend technologies (Node.js, Python, or Java)
- Proficiency in database design (SQL and NoSQL)
- Understanding of RESTful API design principles
- Experience with containerization (Docker) is a plus
- Knowledge of cloud services (AWS, GCP, or Azure)`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Stock Options, Flexible Hours',
    workMode: 'Hybrid'
  },
  {
    jobTitle: 'Full Stack Developer',
    selectedCategory: 'Engineering',
    selectedExperience: '2-5 years',
    skill: 'React, Node.js, TypeScript, PostgreSQL, MongoDB, AWS, Docker, REST APIs, GraphQL',
    jobDescription: `We are hiring a Full Stack Developer to work on end-to-end product development.

Key Responsibilities:
- Develop both frontend and backend components
- Design system architecture and database schemas
- Implement CI/CD pipelines and deployment strategies
- Mentor junior developers and conduct code reviews
- Collaborate with product and design teams

Requirements:
- Strong experience in both frontend (React) and backend (Node.js)
- Proficiency in TypeScript and modern JavaScript
- Experience with relational and NoSQL databases
- Understanding of cloud infrastructure (AWS preferred)
- Excellent problem-solving and communication skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Remote Work, Stock Options, Learning Budget',
    workMode: 'Remote'
  },
  {
    jobTitle: 'Data Scientist',
    selectedCategory: 'Data Science',
    selectedExperience: '1-4 years',
    skill: 'Python, Machine Learning, SQL, Pandas, TensorFlow, PyTorch, Data Visualization, Statistics',
    jobDescription: `We are looking for a Data Scientist to derive insights from complex datasets.

Key Responsibilities:
- Analyze large datasets to identify patterns and trends
- Build and deploy machine learning models
- Create data visualizations and dashboards
- Collaborate with stakeholders to understand business needs
- Present findings and recommendations to non-technical audiences

Requirements:
- Strong proficiency in Python and data science libraries
- Experience with machine learning frameworks (TensorFlow, PyTorch, Scikit-learn)
- Solid understanding of statistics and probability
- Experience with SQL and data manipulation
- Excellent communication and presentation skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Research Budget, Conference Attendance',
    workMode: 'Hybrid'
  },
  {
    jobTitle: 'DevOps Engineer',
    selectedCategory: 'Engineering',
    selectedExperience: '2-5 years',
    skill: 'AWS, Docker, Kubernetes, Terraform, CI/CD, Linux, Python, Monitoring, Jenkins',
    jobDescription: `We are seeking a DevOps Engineer to streamline our development and deployment processes.

Key Responsibilities:
- Design and maintain CI/CD pipelines
- Manage cloud infrastructure using IaC (Terraform, CloudFormation)
- Implement monitoring, logging, and alerting systems
- Optimize system performance and reliability
- Collaborate with development teams on deployment strategies

Requirements:
- Strong experience with AWS or other cloud platforms
- Proficiency in containerization (Docker, Kubernetes)
- Experience with infrastructure as code tools
- Knowledge of scripting languages (Python, Bash)
- Understanding of networking and security best practices`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Certification Budget, Remote Work',
    workMode: 'Remote'
  },
  {
    jobTitle: 'Product Manager',
    selectedCategory: 'Product',
    selectedExperience: '2-5 years',
    skill: 'Product Strategy, User Research, Agile, Data Analysis, Roadmap Planning, Stakeholder Management',
    jobDescription: `We are hiring a Product Manager to drive product vision and strategy.

Key Responsibilities:
- Define product roadmap and prioritize features
- Conduct user research and gather customer feedback
- Work with engineering, design, and marketing teams
- Define success metrics and track product performance
- Communicate product vision to stakeholders

Requirements:
- Experience in product management or related roles
- Strong analytical and problem-solving skills
- Excellent communication and leadership abilities
- Understanding of agile development methodologies
- Ability to translate business needs into product requirements`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Stock Options, Flexible Hours',
    workMode: 'Hybrid'
  },
  {
    jobTitle: 'UI/UX Designer',
    selectedCategory: 'Design',
    selectedExperience: '1-4 years',
    skill: 'Figma, User Research, Wireframing, Prototyping, Design Systems, Usability Testing, Adobe XD',
    jobDescription: `We are looking for a UI/UX Designer to create beautiful and intuitive user experiences.

Key Responsibilities:
- Create wireframes, mockups, and interactive prototypes
- Conduct user research and usability testing
- Design and maintain design systems
- Collaborate with developers to ensure design accuracy
- Stay updated with design trends and best practices

Requirements:
- Proficiency in design tools (Figma, Adobe XD, Sketch)
- Strong portfolio demonstrating UI/UX skills
- Understanding of user-centered design principles
- Experience with design systems and component libraries
- Excellent visual design and typography skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Design Tools Budget, Remote Work',
    workMode: 'Remote'
  },
  {
    jobTitle: 'QA Engineer',
    selectedCategory: 'Engineering',
    selectedExperience: '1-3 years',
    skill: 'Test Automation, Selenium, Cypress, API Testing, SQL, JIRA, Test Planning, Bug Tracking',
    jobDescription: `We are seeking a QA Engineer to ensure the quality of our software products.

Key Responsibilities:
- Design and execute test plans and test cases
- Develop automated test scripts
- Perform manual and automated testing
- Track and report bugs effectively
- Collaborate with developers to resolve issues

Requirements:
- Experience with test automation frameworks (Selenium, Cypress)
- Understanding of software testing methodologies
- Proficiency in API testing tools (Postman, RestAssured)
- Basic knowledge of SQL for database testing
- Strong attention to detail and analytical skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Learning Budget, Flexible Hours',
    workMode: 'Hybrid'
  },
  {
    jobTitle: 'Mobile Developer',
    selectedCategory: 'Engineering',
    selectedExperience: '1-4 years',
    skill: 'React Native, Flutter, iOS, Android, JavaScript, TypeScript, REST APIs, Firebase',
    jobDescription: `We are looking for a Mobile Developer to build cross-platform mobile applications.

Key Responsibilities:
- Develop and maintain mobile applications for iOS and Android
- Write clean, maintainable, and efficient code
- Collaborate with design and backend teams
- Optimize app performance and user experience
- Publish and maintain apps on app stores

Requirements:
- Experience with React Native or Flutter
- Understanding of mobile development best practices
- Knowledge of REST APIs and state management
- Experience with app store deployment
- Strong debugging and problem-solving skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Device Allowance, Remote Work',
    workMode: 'Remote'
  },
  {
    jobTitle: 'Cloud Architect',
    selectedCategory: 'Engineering',
    selectedExperience: '5+ years',
    skill: 'AWS, Azure, GCP, Terraform, Kubernetes, Microservices, Security, Cost Optimization',
    jobDescription: `We are hiring a Cloud Architect to design and implement cloud infrastructure solutions.

Key Responsibilities:
- Design scalable and secure cloud architectures
- Lead cloud migration projects
- Optimize cloud costs and resource utilization
- Define cloud governance and security policies
- Mentor teams on cloud best practices

Requirements:
- Extensive experience with major cloud platforms (AWS, Azure, GCP)
- Strong understanding of cloud architecture patterns
- Experience with infrastructure as code
- Knowledge of security and compliance frameworks
- Excellent leadership and communication skills`,
    compensation: 'Premium',
    benefits: 'Health Insurance, Stock Options, Conference Budget, Remote Work',
    workMode: 'Remote'
  },
  {
    jobTitle: 'Machine Learning Engineer',
    selectedCategory: 'Data Science',
    selectedExperience: '2-5 years',
    skill: 'Python, TensorFlow, PyTorch, MLOps, Docker, Kubernetes, Deep Learning, NLP, Computer Vision',
    jobDescription: `We are seeking a Machine Learning Engineer to build and deploy ML systems at scale.

Key Responsibilities:
- Design and implement ML pipelines
- Deploy models to production environments
- Optimize model performance and inference speed
- Build monitoring and alerting for ML systems
- Collaborate with data scientists and engineers

Requirements:
- Strong experience with ML frameworks (TensorFlow, PyTorch)
- Proficiency in Python and ML libraries
- Experience with MLOps and model deployment
- Understanding of deep learning architectures
- Knowledge of containerization and orchestration`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, GPU Credits, Research Time, Remote Work',
    workMode: 'Remote'
  },
  {
    jobTitle: 'Business Analyst',
    selectedCategory: 'Business',
    selectedExperience: '1-4 years',
    skill: 'Requirements Gathering, SQL, Excel, Data Analysis, JIRA, Agile, Process Mapping, Stakeholder Management',
    jobDescription: `We are looking for a Business Analyst to bridge business needs and technical solutions.

Key Responsibilities:
- Gather and document business requirements
- Analyze business processes and identify improvements
- Create user stories and acceptance criteria
- Work with stakeholders to prioritize features
- Support UAT and change management

Requirements:
- Experience in business analysis or related roles
- Strong analytical and problem-solving skills
- Proficiency in SQL and data analysis tools
- Understanding of agile methodologies
- Excellent communication and documentation skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Learning Budget, Flexible Hours',
    workMode: 'Hybrid'
  },
  {
    jobTitle: 'Technical Support Engineer',
    selectedCategory: 'Support',
    selectedExperience: '0-2 years',
    skill: 'Troubleshooting, Customer Service, Linux, Networking, SQL, JIRA, Documentation, Communication',
    jobDescription: `We are hiring a Technical Support Engineer to provide excellent customer support.

Key Responsibilities:
- Respond to customer technical inquiries
- Troubleshoot and resolve technical issues
- Document solutions and create knowledge base articles
- Escalate complex issues to engineering teams
- Identify patterns and suggest product improvements

Requirements:
- Strong troubleshooting and problem-solving skills
- Basic understanding of Linux and networking
- Excellent written and verbal communication
- Customer-focused mindset
- Ability to work in a fast-paced environment`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Training Programs, Growth Opportunities',
    workMode: 'Remote/Hybrid'
  },
  {
    jobTitle: 'Cybersecurity Analyst',
    selectedCategory: 'Security',
    selectedExperience: '2-5 years',
    skill: 'Security Analysis, SIEM, Penetration Testing, Network Security, Incident Response, Compliance',
    jobDescription: `We are seeking a Cybersecurity Analyst to protect our digital assets and infrastructure.

Key Responsibilities:
- Monitor security systems and investigate alerts
- Conduct vulnerability assessments and penetration testing
- Respond to and document security incidents
- Develop security policies and procedures
- Stay updated on emerging threats and technologies

Requirements:
- Experience in cybersecurity or information security
- Knowledge of security tools (SIEM, IDS/IPS, firewalls)
- Understanding of compliance frameworks (SOC2, ISO 27001)
- Security certifications (CISSP, CEH, CompTIA Security+) preferred
- Strong analytical and communication skills`,
    compensation: 'Competitive',
    benefits: 'Health Insurance, Certification Budget, Remote Work',
    workMode: 'Hybrid'
  }
];

/**
 * Find or create the public JDs company profile
 */
export const getOrCreatePublicCompany = async () => {
  try {
    // Check if public company already exists
    const listRes = await API.graphql(graphqlOperation(listCompanyProfiles, {
      filter: { email: { eq: PUBLIC_COMPANY_CONFIG.email } }
    }));

    if (listRes.data.listCompanyProfiles.items.length > 0) {
      console.log('✅ Public company already exists:', listRes.data.listCompanyProfiles.items[0].id);
      return listRes.data.listCompanyProfiles.items[0];
    }

    // Create new public company
    const createRes = await API.graphql(graphqlOperation(createCompanyProfile, {
      input: PUBLIC_COMPANY_CONFIG
    }));

    console.log('✅ Created public company:', createRes.data.createCompanyProfile.id);
    return createRes.data.createCompanyProfile;

  } catch (error) {
    console.error('❌ Error getting/creating public company:', error);
    throw error;
  }
};

/**
 * Check if a JD already exists for the public company
 */
export const checkExistingJD = async (companyProfileID, jobTitle) => {
  try {
    const listRes = await API.graphql(graphqlOperation(listCompanyJobDescriptions, {
      filter: {
        companyProfileID: { eq: companyProfileID },
        jobTitle: { eq: jobTitle }
      }
    }));

    return listRes.data.listCompanyJobDescriptions.items.length > 0 
      ? listRes.data.listCompanyJobDescriptions.items[0] 
      : null;

  } catch (error) {
    console.error('❌ Error checking existing JD:', error);
    return null;
  }
};

/**
 * Create a single public JD
 */
export const createPublicJD = async (companyProfileID, jdTemplate, index) => {
  try {
    // Check if JD already exists
    const existingJD = await checkExistingJD(companyProfileID, jdTemplate.jobTitle);
    if (existingJD) {
      console.log(`⏭️ JD already exists for ${jdTemplate.jobTitle}:`, existingJD.id);
      return existingJD;
    }

    const input = {
      companyProfileID: companyProfileID,
      jobIDNo: 100 + index, // Starting from 100 for public JDs
      jobTitle: jdTemplate.jobTitle,
      selectedCategory: jdTemplate.selectedCategory,
      selectedExperience: jdTemplate.selectedExperience,
      skill: jdTemplate.skill,
      jobDescription: jdTemplate.jobDescription,
      compensation: jdTemplate.compensation,
      benefits: jdTemplate.benefits,
      workMode: jdTemplate.workMode
    };

    const res = await API.graphql(graphqlOperation(createCompanyJobDescription, { input }));
    console.log(`✅ Created public JD: ${jdTemplate.jobTitle}`, res.data.createCompanyJobDescription.id);
    return res.data.createCompanyJobDescription;

  } catch (error) {
    console.error(`❌ Error creating JD for ${jdTemplate.jobTitle}:`, error);
    throw error;
  }
};

/**
 * Seed all public JDs into the database
 * Call this function once to populate the database
 */
export const seedPublicJDs = async () => {
  console.log('🚀 Starting public JD seed process...');
  
  try {
    // Step 1: Get or create public company
    const publicCompany = await getOrCreatePublicCompany();
    console.log('📦 Public company ID:', publicCompany.id);

    // Step 2: Create all JDs
    const createdJDs = [];
    for (let i = 0; i < PUBLIC_JD_TEMPLATES.length; i++) {
      const jd = await createPublicJD(publicCompany.id, PUBLIC_JD_TEMPLATES[i], i);
      createdJDs.push(jd);
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    console.log('🎉 Successfully seeded all public JDs!');
    console.log(`📊 Total JDs created/found: ${createdJDs.length}`);
    
    return {
      company: publicCompany,
      jds: createdJDs
    };

  } catch (error) {
    console.error('❌ Error seeding public JDs:', error);
    throw error;
  }
};

/**
 * Fetch all public JDs for candidate selection
 */
export const fetchPublicJDs = async () => {
  try {
    // First get public company
    const listCompanyRes = await API.graphql(graphqlOperation(listCompanyProfiles, {
      filter: { email: { eq: PUBLIC_COMPANY_CONFIG.email } }
    }));

    if (listCompanyRes.data.listCompanyProfiles.items.length === 0) {
      console.warn('⚠️ Public company not found. Run seedPublicJDs() first.');
      return [];
    }

    const publicCompanyID = listCompanyRes.data.listCompanyProfiles.items[0].id;

    // Fetch all JDs for public company
    let allJDs = [];
    let nextToken = null;

    do {
      const listJDsRes = await API.graphql(graphqlOperation(listCompanyJobDescriptions, {
        filter: { companyProfileID: { eq: publicCompanyID } },
        limit: 100,
        nextToken: nextToken
      }));

      allJDs = [...allJDs, ...listJDsRes.data.listCompanyJobDescriptions.items];
      nextToken = listJDsRes.data.listCompanyJobDescriptions.nextToken;

    } while (nextToken);

    console.log(`✅ Fetched ${allJDs.length} public JDs`);
    return allJDs;

  } catch (error) {
    console.error('❌ Error fetching public JDs:', error);
    return [];
  }
};

// Export the config for use in other files
export { PUBLIC_COMPANY_CONFIG };

// Make seedPublicJDs available globally for browser console execution
if (typeof window !== 'undefined') {
  window.seedPublicJDs = seedPublicJDs;
  window.fetchPublicJDs = fetchPublicJDs;
}
